import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaCode } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

interface HeaderProps {
  currentSection?: string;
}

const Header: React.FC<HeaderProps> = ({ currentSection = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        if (currentSection) {
            setActiveSection(currentSection);
        }
    }, [currentSection]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setScrolled(scrollPosition > 50);

            if (!currentSection) {
                const sections = ['home', 'about', 'skills', 'projects', 'education', 'work', 'contact'];

                for (const section of sections.reverse()) {
                    const element = document.getElementById(section);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        if (rect.top <= 100) {
                            setActiveSection(section);
                            break;
                        }
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [currentSection]);

    const scrollTo = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setIsOpen(false);
    };

    const { t } = useTranslation();
    
    const menuItems = [
        { id: 'home', label: t('navigation.home') },
        { id: 'about', label: t('navigation.about') },
        { id: 'skills', label: t('navigation.skills') },
        { id: 'projects', label: t('navigation.projects') },
        { id: 'education', label: t('navigation.education') },
        { id: 'work', label: t('navigation.work') },
        { id: 'contact', label: t('navigation.contact') }
    ];

    const navVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: -10 },
        visible: { opacity: 1, y: 0 }
    };

    const mobileMenuVariants = {
        hidden: { opacity: 0, height: 0 },
        visible: {
            opacity: 1,
            height: 'auto',
            transition: {
                duration: 0.3,
                staggerChildren: 0.05
            }
        },
        exit: {
            opacity: 0,
            height: 0,
            transition: {
                duration: 0.3
            }
        }
    };

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
            scrolled ? 'py-2 backdrop-blur-md bg-gray-900/90 shadow-xl border-b border-gray-800/50' : 'py-4 bg-transparent'
        }`}>
            <nav className="responsive-container">
                <div className="flex items-center justify-between h-16">
                    <motion.div
                        className="flex-shrink-0"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, type: "spring" }}
                    >
                        <motion.div
                            className="flex items-center space-x-2 cursor-pointer"
                            onClick={() => scrollTo('home')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <motion.div 
                                className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 flex items-center justify-center shadow-lg"
                                animate={{ 
                                    boxShadow: ["0px 0px 8px rgba(99, 102, 241, 0.4)", "0px 0px 20px rgba(99, 102, 241, 0.6)", "0px 0px 8px rgba(99, 102, 241, 0.4)"]
                                }}
                                transition={{ 
                                    duration: 3, 
                                    repeat: Infinity,
                                    repeatType: "reverse"
                                }}
                            >
                                <FaCode className="text-white text-lg" />
                            </motion.div>
                            <span className="text-xl font-bold text-white hidden sm:block">Feres</span>
                        </motion.div>
                    </motion.div>

                    {/* Medium screen navigation (md to lg) */}
                    <motion.div
                        className="hidden md:block lg:hidden"
                        initial="hidden"
                        animate="visible"
                        variants={navVariants}
                    >
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1">
                                {menuItems.map((item) => (
                                    <motion.button
                                        key={item.id}
                                        onClick={() => scrollTo(item.id)}
                                        className={`px-2 py-2 rounded-lg text-xs font-medium transition-all duration-300 relative group ${
                                            activeSection === item.id
                                                ? 'text-white'
                                                : 'text-gray-400 hover:text-white'
                                        }`}
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {item.label}
                                        
                                        {activeSection === item.id && (
                                            <motion.div
                                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                                                layoutId="activeSection"
                                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-gray-800 bg-opacity-0 rounded-lg group-hover:bg-opacity-30 transition-all duration-300 -z-10"></div>
                                    </motion.button>
                                ))}
                            </div>
                            <LanguageSwitcher />
                        </div>
                    </motion.div>

                    {/* Large screen navigation (lg and up) */}
                    <motion.div
                        className="hidden lg:block"
                        initial="hidden"
                        animate="visible"
                        variants={navVariants}
                    >
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                                {menuItems.map((item) => (
                                    <motion.button
                                        key={item.id}
                                        onClick={() => scrollTo(item.id)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative group ${
                                            activeSection === item.id
                                                ? 'text-white'
                                                : 'text-gray-400 hover:text-white'
                                        }`}
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {item.label}
                                        
                                        {activeSection === item.id && (
                                            <motion.div
                                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
                                                layoutId="activeSection"
                                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-gray-800 bg-opacity-0 rounded-lg group-hover:bg-opacity-30 transition-all duration-300 -z-10"></div>
                                    </motion.button>
                                ))}
                            </div>
                            <LanguageSwitcher />
                        </div>
                    </motion.div>

                    <div className="md:hidden flex items-center space-x-2">
                        <LanguageSwitcher />
                        <motion.button
                            onClick={() => setIsOpen(!isOpen)}
                            className={`p-2 rounded-full ${
                                isOpen ? 'bg-gray-800' : 'hover:bg-gray-800 hover:bg-opacity-50'
                            } text-gray-300 hover:text-white focus:outline-none transition-all duration-300`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            {isOpen ? <FaTimes /> : <FaBars />}
                        </motion.button>
                    </div>
                </div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="md:hidden overflow-hidden"
                            variants={mobileMenuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="px-2 pt-2 pb-3 space-y-1 glass rounded-xl mt-2 border border-gray-800 border-opacity-50">
                                {menuItems.map((item) => (
                                    <motion.button
                                        key={item.id}
                                        onClick={() => scrollTo(item.id)}
                                        className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
                                            activeSection === item.id
                                                ? 'text-white bg-gray-800 bg-opacity-50'
                                                : 'text-gray-300 hover:bg-gray-800 hover:bg-opacity-30 hover:text-white'
                                        }`}
                                        variants={itemVariants}
                                        whileHover={{ x: 5 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        {item.label}
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>
        </header>
    );
};

export default Header;
